import React, { useState } from "react"
import Swal from "sweetalert2"
import IconButton from "../../components/IconButton"
import DeleteModal from "../../components/Harmonization/DeleteModal"
import EyeIcon from "../../icons/harmonization/EyeIcon"
import DeleteIcon from "../../icons/harmonization/DeleteIcon"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  deleteMappingAsync,
  getScenarioDetails,
  getScenarioList,
  viewDemoMapping,
} from "./HarmonizationSlice"
import UpTriangleIcon from "../../icons/UpTriangleGrey.svg"
import DownTriangleIcon from "../../icons/DownTriangleGrey.svg"
import { useNavigate } from "react-router-dom"
import { SCREENS_DETAILS } from "../../util/StaticData"
import { setShowMappingSummary } from "./mapping/MapSlice"

export interface TableRowData {
  [key: string]: any
}

interface IHeader {
  key: string
  label: string
}

interface ITableHeaderProps {
  headers: IHeader[]
}

const TableHeader: React.FC<ITableHeaderProps> = ({ headers }) => (
  <thead className="w-full border-b-2 border-border-100">
    <tr className="w-full">
      {headers.map(({ key, label }, index) => (
        <th
          key={index + key}
          className="px-3 py-2 text-[11px] text-textGrey-50"
        >
          <div
            className={`flex gap-2 ${
              [4, 5].includes(index) ? "justify-center" : "justify-left"
            }`}
          >
            {label}
            {index !== 0 && index !== 8 && index !== 9 && (
              <div className="flex flex-col w-2">
                <img className="h-2" src={UpTriangleIcon} alt="up" />
                <img className="h-2" src={DownTriangleIcon} alt="down" />
              </div>
            )}
          </div>
        </th>
      ))}
    </tr>
  </thead>
)

interface TableRowProps {
  row: TableRowData
  onDelete: (id: string) => void
}

const TableRow: React.FC<TableRowProps> = ({ row, onDelete }) => {
  const {
    id,
    sourceDataset,
    targetDataset,
    sourceColumns,
    accuracy,
    createdBy,
    createdAt,
    executionTime,
    executionStatus: status,
  } = row
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const DemoMapAccuracy = useAppSelector((state) => state.map.mappingAccuracy)

const timeFormatter = (time: number) => {
  const hours = Math.floor(time / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = Math.floor(time % 60)

  if (hours > 0) {
    return `${hours} hours ${minutes} min`
  } else if (minutes > 0) {
    return `${minutes} min ${seconds} sec`
  } else {
    return `${seconds} sec`
  }
}



  const formatDate = (date: string) => {
    const newDate = new Date(date)
    const day = newDate.getDate().toString().padStart(2, "0")
    const month = (newDate.getMonth() + 1).toString().padStart(2, "0")
    const year = newDate.getFullYear()

    return `${day}-${month}-${year}`
  }

  const statusFormatter = (status: number) => {
    switch (status) {
      case 0:
        return "Failed"
      case 1:
        return "In Progress"
      case 2:
        return "Unknown"
      case 3:
        return "Completed"
      case 4:
        return "Scheduled"
      default:
        return "Inactive"
    }
  }

  const getStatusStyles = () => {
    switch (status) {
      case 0:
        return "bg-bgRed-150 text-customRed-50"
      case 1:
        return "bg-background-processing-50 text-text-status-100"
      case 3:
        return "bg-background-success-50 text-text-status-150"
      default:
        return "bg-background-grey-100 text-text-status-50"
    }
  }

  const handleViewClick = (id:number) => {
    navigate(`${SCREENS_DETAILS.CREATE_SCENARIO}/${id}`)

  }

  const today = new Date()

  return (
    <tr className="text-xs border-b border-border-150">
      {/* <td className="px-2"><PlusIcon /></td> */}
      <td className="font-bold p-3">{sourceDataset}</td>
      <td className="font-bold px-3">{targetDataset}</td>
      <td className="px-3 py-1">
        <div className="flex gap-1">
          {sourceColumns?.map((item: string, index: number) => (
            <div
              key={index}
              className="w-fit flex justify-center items-center px-1.5 py-0.5 bg-background-grey-50"
            >
              <p>{item}</p>
            </div>
          ))}
        </div>
      </td>
      {/* <td className="px-3 text-center">{Math.round(accuracy)}%</td> */}
      <td className="px-3">{createdBy}</td>
      <td className="px-3">{formatDate(id === 55 ? today : createdAt)}</td>
      <td className="px-3 text-center">{timeFormatter(executionTime)}</td>
      <td className="px-3">
        <div
          className={`h-[22px] rounded w-fit flex justify-center items-center px-2 font-bold ${getStatusStyles()}`}
        >
          {statusFormatter(status)}
        </div>
      </td>
      <td className="px-3">
        <div className="flex gap-1">
          <IconButton
            Icon={EyeIcon}
            onClick={() => handleViewClick(id)}
            ariaLabel="View"
          />
          <IconButton
            Icon={DeleteIcon}
            onClick={() => onDelete(id)}
            ariaLabel="Delete"
          />
        </div>
      </td>
    </tr>
  )
}

const ScenarioTable: React.FC<{ data: TableRowData[] }> = ({ data }) => {
  const dispatch = useAppDispatch()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const { saveClicked } = useAppSelector((state) => state.map)

  const headers: IHeader[] = [
    // { key: "expand", label: "" },
    { key: "sourceDataset", label: "Dataset 1" },
    { key: "targetDataset", label: "Dataset 2" },
    { key: "sourceColumns", label: "Columns Mapped" },
    // { key: "accuracy", label: "Mapping Accuracy" },
    { key: "createdBy", label: "Owner" },
    { key: "createdAt", label: "Date" },
    { key: "executionTime", label: "Execution Time" },
    { key: "executionStatus", label: "Current Status" },
    { key: "actions", label: "Actions" },
  ]

  const deleteWarning = (id: string) => {
    setIsDeleteModalOpen(true)
    setDeleteId(id)
  }

  const handleDeleteMapping = () => {
    if (deleteId) {
      dispatch(deleteMappingAsync({ id: deleteId })).then(() => {
        Swal.fire({
          title: "Scenario Mapping Deleted Successfully",
          icon: "success",
          timer: 1000,
          iconColor: "#2FD4CF",
          showConfirmButton: false,
          willClose: () => dispatch(getScenarioList()),
          customClass: {
            title: "custom-title",
          },
        })
      })
    }
    setIsDeleteModalOpen(false)
  }

  return (
    <div className="">
      <style>
        {`
          .custom-title {
            font-size: 20px !important; /* Override SweetAlert2 default */
          }
        `}
      </style>
      <table
        className="w-full"
        style={{ whiteSpace: "nowrap", borderCollapse: "collapse" }}
      >
        <TableHeader headers={headers} />
        <tbody>
          {data?.map(
            (row, index) =>
              (row.id !== 55 || saveClicked) && (
                <TableRow key={index} row={row} onDelete={deleteWarning} />
              ),
          )}
        </tbody>
      </table>
      <DeleteModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        handleDeleteMapping={handleDeleteMapping}
      />
    </div>
  )
}

export default ScenarioTable
